import type { NavigationItem, NavigationItemText } from '@unifyapps/defs/types/interface';
import {
  type ApplicationType,
  type InterfaceEntity,
  NavigationBarType,
  NavigationItemAlignment,
  NavigationItemIconSize,
  NavigationItemLabelVariant,
  NavigationPosition,
  type NavigationResolution,
} from '@unifyapps/defs/types/interface';
import { typographyVariants } from '@unifyapps/ui/components/Typography/styles';
import clsx from 'clsx';
import _omit from 'lodash/omit';
import _pick from 'lodash/pick';
import { EMPTY_OBJECT } from '../../consts/empty';
import { InterfaceNavigationHelper } from '../../helpers/InterfaceNavigationHelper';
import { useBlockAppearanceStyles } from './useBlockAppearanceStyles';

export const useSideNavParams = ({
  interfaceRecord,
  resolution,
  applicationType,
}: {
  interfaceRecord?: InterfaceEntity;
  resolution: NavigationResolution;
  applicationType?: ApplicationType;
}) => {
  const navigationBarType = interfaceRecord
    ? InterfaceNavigationHelper.getBarTypeByResolution(interfaceRecord, resolution)
    : NavigationBarType.Fixed;

  const position = interfaceRecord
    ? InterfaceNavigationHelper.getPositionByResolution(interfaceRecord, resolution)
    : NavigationPosition.Start;

  const navigationItemAppearance = interfaceRecord
    ? InterfaceNavigationHelper.getItemAppearance(interfaceRecord, resolution)
    : undefined;

  const theme = interfaceRecord
    ? InterfaceNavigationHelper.getThemeByResolution(interfaceRecord, resolution)
    : undefined;

  const navigatorType = interfaceRecord
    ? InterfaceNavigationHelper.getNavigatorTypeByResolution(
        interfaceRecord,
        resolution,
        applicationType,
      )
    : undefined;

  const styles = InterfaceNavigationHelper.getStylesByResolution(
    interfaceRecord,
    resolution,
    position,
    applicationType,
  );

  const { fontFamily, variant, weight } =
    navigationItemAppearance?.text ?? (EMPTY_OBJECT as NavigationItemText);

  const typographyClassName = clsx(fontFamily, typographyVariants({ variant, weight }));

  const { className: floatingOffset } = useBlockAppearanceStyles({
    appearanceStyles: interfaceRecord
      ? InterfaceNavigationHelper.getFloatingOffsetByResolution(interfaceRecord, resolution)
      : undefined,
  });

  const { className, style } = useBlockAppearanceStyles({
    appearanceStyles: _pick(styles, [
      'borderColor',
      'borderWidth',
      'borderRadius',
      'borderStyle',
      'width',
      'padding',
      'boxShadow',
      'gap',
    ]),
  });

  const { className: containerClassName, style: containerStyles } = useBlockAppearanceStyles({
    appearanceStyles: _pick(styles, ['height', 'width']),
  });

  const { className: heightClassName, style: heightStyles } = useBlockAppearanceStyles({
    appearanceStyles: _pick(styles, ['height']),
  });

  const { className: widthClassName, style: widthStyles } = useBlockAppearanceStyles({
    appearanceStyles: _pick(styles, ['width']),
  });

  const { className: borderColorClassName } = useBlockAppearanceStyles({
    appearanceStyles: _pick(styles, ['borderColor']),
  });

  const { className: borderClassName, style: borderStyles } = useBlockAppearanceStyles({
    appearanceStyles: _pick(styles, ['borderColor', 'borderWidth', 'borderRadius']),
  });

  const { className: navItemsGapClassName, style: navItemsGapStyles } = useBlockAppearanceStyles({
    appearanceStyles: _pick(styles, ['gapSecondary']),
  });

  const navItemStylesEnabled =
    navigationItemAppearance?.labelVariant === NavigationItemLabelVariant.Upfront &&
    position === NavigationPosition.Start;

  const { className: navItemClassName, style: navItemStyles } = useBlockAppearanceStyles({
    appearanceStyles: navItemStylesEnabled
      ? _omit(navigationItemAppearance.styles, ['borderRadius'])
      : undefined,
  });

  const { className: navItemBorderRadiusClassName, style: navItemBorderRadiusStyles } =
    useBlockAppearanceStyles({
      appearanceStyles: _pick(navigationItemAppearance?.styles, ['borderRadius']),
    });

  const { labelVariant, icon } = navigationItemAppearance ?? {};

  // TODO : Remove this once we add support for Alignment in NavigationItem
  // For expandable variant, icon size fixed to MD
  const adaptedNavigationItemAppearance = {
    ...navigationItemAppearance,
    alignment: NavigationItemAlignment.Start,
    icon: {
      ...navigationItemAppearance?.icon,
      size:
        labelVariant === NavigationItemLabelVariant.Expandable
          ? NavigationItemIconSize.MD
          : icon?.size,
    },
  } as NavigationItem;

  return {
    navigationProps: {
      navigationBarType,
      position,
      navigationItemAppearance: adaptedNavigationItemAppearance,
      theme,
      floatingOffset,
      navigatorType,
      resolution,
    },
    navigationStyles: {
      style,
      containerStyles,
      heightStyles,
      widthStyles,
      navItemStyles,
      navItemsGapStyles,
      navItemBorderRadiusStyles,
    },
    navigationClassNames: {
      className,
      containerClassName,
      widthClassName,
      heightClassName,
      borderColorClassName,
      typographyClassName,
      navItemClassName,
      navItemsGapClassName,
      navItemBorderRadiusClassName,
    },
    borderProps: {
      className: borderClassName,
      style: borderStyles,
    },
  };
};
